import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Fade,
  CircularProgress,
  Avatar
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const localizer = momentLocalizer(moment);

const defaultMessages = {
  date: "Data",
  time: "Hora",
  event: "Evento",
  allDay: "Dia Todo",
  week: "Semana",
  work_week: "Agendamentos",
  day: "Dia",
  month: "Mês",
  previous: "Anterior",
  next: "Próximo",
  yesterday: "Ontem",
  tomorrow: "Amanhã",
  today: "Hoje",
  agenda: "Agenda",
  noEventsInRange: "Não há agendamentos no período.",
  showMore: (total) => `+${total} mais`
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_SCHEDULES": {
      const schedules = action.payload;
      const newSchedules = [];
      schedules.forEach((schedule) => {
        const idx = state.findIndex((s) => s.id === schedule.id);
        if (idx !== -1) {
          state[idx] = schedule;
        } else {
          newSchedules.push(schedule);
        }
      });
      return [...state, ...newSchedules];
    }
    case "UPDATE_SCHEDULES": {
      const schedule = action.payload;
      const idx = state.findIndex((s) => s.id === schedule.id);
      if (idx !== -1) {
        state[idx] = schedule;
        return [...state];
      }
      return [schedule, ...state];
    }
    case "DELETE_SCHEDULE": {
      const idx = state.findIndex((s) => s.id === action.payload);
      if (idx !== -1) state.splice(idx, 1);
      return [...state];
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Schedules
// ---------------------------------------------------------------------------
const Schedules = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));

  // ---------------------------------------------------------------------------
  // Permissão do plano
  // ---------------------------------------------------------------------------
  useEffect(() => {
    async function checkPlan() {
      const planConfigs = await getPlanCompany(undefined, user.companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    checkPlan();
  }, [user, history, getPlanCompany]);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, pageNumber }
      });
      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => { fetchSchedules(); }, 500);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber, contactId, fetchSchedules]);

  // ---------------------------------------------------------------------------
  // Socket
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    };
    socket.on(`company${user.companyId}-schedule`, onCompanySchedule);
    return () => socket.off(`company${user.companyId}-schedule`, onCompanySchedule);
  }, [socket, user.companyId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const cleanContact = () => setContactId("");

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <CalendarMonthIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("schedules.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                {schedules.length} agendamento{schedules.length !== 1 ? "s" : ""} cadastrado{schedules.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            {/* Search */}
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: 200, sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                  fontSize: "0.875rem",
                  "& fieldset": { borderColor: "divider" },
                  "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                  "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                }
              }}
            />

            {/* Add button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenScheduleModal}
              sx={{
                bgcolor: PRIMARY_MAIN,
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                textTransform: "none",
                "&:hover": {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                }
              }}
            >
              {i18n.t("schedules.buttons.add")}
            </Button>
          </Stack>
        </Stack>

        {/* Calendar */}
        <Paper
          elevation={0}
          onScroll={handleScroll}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            p: { xs: 2, md: 3 },
            mb: 3,
            overflowY: "auto",
            maxHeight: "calc(100vh - 220px)",

            // Toolbar
            "& .rbc-toolbar": {
              mb: 2,
              flexWrap: "wrap",
              gap: 1
            },
            "& .rbc-toolbar-label": {
              fontWeight: 700,
              fontSize: "1.125rem",
              color: "text.primary",
              letterSpacing: "-0.3px"
            },
            "& .rbc-btn-group button": {
              border: "1px solid",
              borderColor: "divider !important",
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "text.secondary",
              transition: "all 0.2s ease",
              "&:first-of-type": { borderRadius: "8px 0 0 8px" },
              "&:last-of-type": { borderRadius: "0 8px 8px 0" },
              "&:hover": {
                bgcolor: "rgba(25, 118, 210, 0.08)",
                color: PRIMARY_MAIN
              },
              "&.rbc-active": {
                bgcolor: `${PRIMARY_MAIN} !important`,
                color: "#fff !important",
                borderColor: `${PRIMARY_MAIN} !important`
              }
            },
            "& .rbc-today": {
              bgcolor: "rgba(25, 118, 210, 0.04)"
            },

            // Header row
            "& .rbc-header": {
              fontWeight: 600,
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "text.secondary",
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider"
            },

            // Day cells
            "& .rbc-day-cell": {
              borderColor: "divider",
              "&:hover": { bgcolor: "rgba(25, 118, 210, 0.02)" }
            },
            "& .rbc-date-cell": {
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "text.secondary",
              pt: 0.5,
              px: 0.75
            },
            "& .rbc-date-cell.rbc-today a": {
              color: PRIMARY_MAIN,
              fontWeight: 700
            },

            // Events
            "& .rbc-event": {
              bgcolor: `${PRIMARY_MAIN} !important`,
              borderRadius: "4px !important",
              border: "none !important",
              padding: "2px 6px !important",
              fontSize: "0.75rem !important",
              fontWeight: 600,
              color: "#fff !important",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                filter: "brightness(1.1)",
                transform: "translateY(-1px)"
              }
            },
            "& .rbc-event-content": {
              display: "flex",
              alignItems: "center",
              gap: "4px",
              overflow: "hidden"
            },

            // +more link
            "& .rbc-show-more": {
              color: PRIMARY_MAIN,
              fontWeight: 600,
              fontSize: "0.75rem !important",
              "&:hover": { textDecoration: "underline" }
            },

            // Off-range
            "& .rbc-off-date": {
              opacity: 0.45
            }
          }}
        >
          <Calendar
            messages={defaultMessages}
            formats={{
              agendaDateFormat: "DD/MM ddd",
              weekdayFormat: "dddd"
            }}
            localizer={localizer}
            events={schedules.map((schedule) => ({
              title: schedule?.contact?.name || "Agendamento",
              start: new Date(schedule.sendAt),
              end: new Date(schedule.sendAt),
              resource: schedule
            }))}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            style={{ height: 580 }}
            onSelectEvent={(event) => {
              handleEditSchedule(event.resource);
            }}
          />
        </Paper>

        {/* Listagem de agendamentos */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              Todos os Agendamentos
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            )}

            {!loading && schedules.length === 0 && (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                  Nenhum agendamento encontrado.
                </Typography>
              </Box>
            )}

            <Stack spacing={1.5}>
              {!loading && schedules.map((schedule) => {
                const sendDate = moment(schedule.sendAt);
                const isPast = sendDate.isBefore(moment());

                return (
                  <Fade in={true} key={schedule.id} timeout={300}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#fff",
                        border: "1px solid",
                        borderColor: isPast ? "rgba(148,163,184,0.25)" : "divider",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        transition: "all 0.2s ease",
                        opacity: isPast ? 0.65 : 1,
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          transform: "translateX(2px)"
                        }
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2 }}>
                        {/* Stripe */}
                        <Box
                          sx={{
                            width: 3,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: isPast ? "#94a3b8" : PRIMARY_MAIN,
                            flexShrink: 0
                          }}
                        />

                        {/* Date badge */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            minWidth: 48,
                            bgcolor: isPast ? "rgba(148, 163, 184, 0.08)" : "rgba(25, 118, 210, 0.08)",
                            borderRadius: 2,
                            py: 0.75,
                            px: 1
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: isPast ? "text.disabled" : PRIMARY_MAIN, fontSize: "0.65rem" }}>
                            {sendDate.format("MMM")}
                          </Typography>
                          <Typography variant="h6" fontWeight={700} sx={{ color: isPast ? "text.secondary" : PRIMARY_MAIN, lineHeight: 1.2, fontSize: "1.125rem" }}>
                            {sendDate.format("DD")}
                          </Typography>
                        </Box>

                        {/* Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: "0.875rem" }}>
                            {schedule?.contact?.name || "Sem nome"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                            {sendDate.format("DD/MM/YYYY [às] HH:mm")}
                          </Typography>
                        </Box>

                        {/* Status chip */}
                        {isPast && (
                          <Chip
                            label="Enviado"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              bgcolor: "rgba(16, 185, 129, 0.1)",
                              color: "#10b981",
                              border: "1px solid rgba(16, 185, 129, 0.2)"
                            }}
                          />
                        )}
                        {!isPast && (
                          <Chip
                            label="Pendente"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              bgcolor: "rgba(245, 158, 11, 0.1)",
                              color: "#f59e0b",
                              border: "1px solid rgba(245, 158, 11, 0.2)"
                            }}
                          />
                        )}

                        {/* Ações */}
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditSchedule(schedule)}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { color: PRIMARY_MAIN, bgcolor: "rgba(25, 118, 210, 0.08)" }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeletingSchedule(schedule);
                              setConfirmModalOpen(true);
                            }}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.08)" }
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Fade>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      </Container>

      {/* Modais */}
      <ConfirmationModal
        title={deletingSchedule ? i18n.t("schedules.confirmationModal.deleteTitle") : ""}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          scheduleId={selectedSchedule ? selectedSchedule.id : null}
          contactId={contactId}
          cleanContact={cleanContact}
        />
      )}
    </Box>
  );
};

export default Schedules;